// paginatable.ts
import { A } from "@adornis/base/env-info.js";
import { ArrayBehaviorSubject } from "@adornis/base/rxjs-extension/index.js";
import { getRawCollection } from "@adornis/baseql/server/collections.js";
import "@adornis/baseql/utils/queryGeneration.js";
import {
  BehaviorSubject,
  Subject,
  combineLatest,
  concatMap,
  filter,
  map,
  scan,
  shareReplay,
  skip,
  switchMap,
  takeUntil,
  tap
} from "rxjs";
var Pagination = class {
  constructor(queryOrSubscription, args, selectionSet, initialPagination = 20, paginationStep = 20) {
    this.initialPagination = initialPagination;
    this.paginationStep = paginationStep;
    this.totalRequested = new BehaviorSubject(this.initialPagination);
    this.complete = new BehaviorSubject(false);
    this.parameters = new BehaviorSubject(args);
    this.unsubscribe = new Subject();
    this.data = new ArrayBehaviorSubject([]);
    this.loadingInitially = new BehaviorSubject(true);
    this.parameters.pipe(skip(1)).subscribe(() => {
      this._reset();
    });
  }
  next() {
    this.totalRequested.next(this.totalRequested.getValue() + this.paginationStep);
  }
  updateParameters(...args) {
    this.parameters.next(args);
  }
  stop() {
    this.unsubscribe.next();
  }
  _reset() {
    this.totalRequested.next(this.initialPagination);
    this.complete.next(false);
    this.loadingInitially.next(true);
  }
};
var QueryPagination = class extends Pagination {
  constructor(query, args, selectionSet, initialPagination, paginationStep) {
    super(query, args, selectionSet, initialPagination, paginationStep);
    this.totalRequested.pipe(
      scan((acc, val) => acc.concat([val]).slice(-2), [0, 0]),
      concatMap(([oldTotal, total]) => {
        if (total <= oldTotal) {
          this.loadingInitially.next(true);
          this.data.next([]);
          return query(0, total, ...this.parameters.getValue())(selectionSet);
        }
        return query(total - this.paginationStep, this.paginationStep, ...this.parameters.getValue())(selectionSet);
      }),
      tap(() => requestAnimationFrame(() => this.loadingInitially.next(false))),
      takeUntil(this.unsubscribe)
    ).subscribe((entries) => {
      this.data.concat(entries);
      if (this.data.length < this.totalRequested.getValue())
        this.complete.next(true);
    });
  }
};
var SubscriptionPagination = class extends Pagination {
  constructor(subscription, args, selectionSet, initialPagination, paginationStep) {
    super(subscription, args, selectionSet, initialPagination, paginationStep);
    this._subscriptionObservables = new ArrayBehaviorSubject([]);
    this._stopInternalSubsriptions = new Subject();
    this.totalRequested.pipe(
      scan((acc, val) => acc.concat([val]).slice(-2), [0, 0]),
      takeUntil(this.unsubscribe)
    ).subscribe(([oldTotal, total]) => {
      if (total <= oldTotal) {
        this.loadingInitially.next(true);
        this._stopInternalSubsriptions.next();
        return this._subscriptionObservables.next([
          subscription(
            0,
            total,
            ...this.parameters.getValue()
          )(selectionSet).pipe(takeUntil(this._stopInternalSubsriptions), shareReplay(1))
        ]);
      }
      this._subscriptionObservables.push(
        subscription(
          total - this.paginationStep,
          this.paginationStep,
          ...this.parameters.getValue()
        )(selectionSet).pipe(takeUntil(this._stopInternalSubsriptions), shareReplay(1))
      );
    });
    this._subscriptionObservables.pipe(
      filter((obs) => !!obs.length),
      switchMap((obs) => combineLatest(obs)),
      map((dataArrays) => dataArrays.flat()),
      takeUntil(this.unsubscribe)
    ).subscribe((data) => {
      this.data.next(data);
      this.loadingInitially.next(false);
      if (data.length < this.totalRequested.getValue())
        this.complete.next(true);
    });
  }
  stop() {
    super.stop();
    this._stopInternalSubsriptions.next();
  }
};
var EmptyPagination = class extends Pagination {
  constructor() {
    super();
    this.totalRequested = new BehaviorSubject(0);
    this.parameters = new BehaviorSubject([]);
    this.data = new ArrayBehaviorSubject([]);
    this.loadingInitially = new BehaviorSubject(false);
  }
  next() {
  }
  updateParameters() {
  }
};
function getPaginatedQuery(entity, queryMethodReference, args, initialDocumentNumber = 20, paginationStep) {
  return (gqlFields) => new QueryPagination(
    queryMethodReference.bind(entity),
    args,
    gqlFields,
    initialDocumentNumber,
    paginationStep
  );
}
function getPaginatedSubscription(entity, subscriptionMethodReference, args, initialDocumentNumber = 20, paginationStep) {
  return (gqlFields) => new SubscriptionPagination(
    subscriptionMethodReference.bind(entity),
    args,
    gqlFields,
    initialDocumentNumber,
    paginationStep
  );
}
function generateFilterQuery(entity, search) {
  if (!A.isServer)
    throw new Error(
      "'generateFilterQuery' is a server-side helper function to generate a MongoDB FilterQuery[] for a given entity"
    );
  return Object.entries(search.toObject()).filter(([key]) => key !== "_class").filter(([key]) => !entity.fields.find((field) => field.name === key)?.options.resolve).map(([key, val]) => {
    if (typeof val === "string")
      return val ? { [key]: { $regex: val, $options: "i" } } : void 0;
    if (typeof val === "number" || typeof val === "boolean")
      return { [key]: val };
    return void 0;
  }, {}).filter(Boolean);
}
async function paginatedSearchQueryHelper(entity, search, skip2, limit) {
  if (!A.isServer)
    throw new Error(
      "'paginatedSearchQueryHelper' is a server-side helper function to generate a MongoDB FilterQuery for a given entity"
    );
  const db = await getRawCollection(entity._collectionName);
  const filterQuery = generateFilterQuery(entity, search);
  return db.find(filterQuery.length ? { $or: filterQuery } : {}, { skip: skip2, limit }).toArray();
}
export {
  EmptyPagination,
  Pagination,
  QueryPagination,
  SubscriptionPagination,
  generateFilterQuery,
  getPaginatedQuery,
  getPaginatedSubscription,
  paginatedSearchQueryHelper
};
